import { isGovernmentPayorOrder } from './gearOrderUtil';

/**
 * A utility object for common workflow logic.
 * This utility can be used anywhere (unlike the Higher-Order Component 'withWorkflow')
 */

/* The defined workflow steps
 *   id: The GearStep.stepId
 *   route: The url path of this step
 *   iconClassName: The CSS icon class to display in the StepButton
 *   breadcrumb: The text to display in the breadcrumb and in the StepButton
 */
const workflowMetaData = {
	practitioner: {
		id: 1,
		route: 'pract',
		iconClassName: 'fa-user-md',
		breadcrumb: 'Practitioner',
	},
	panel: {
		id: 2,
		route: 'panels',
		iconClassName: 'fa-clipboard',
		breadcrumb: 'Panel Selection',
	},
	billing: {
		id: 3,
		route: 'billing',
		iconClassName: 'fa-credit-card',
		breadcrumb: 'Billing Options',
	},
	diagnosis: {
		id: 4,
		route: 'dxCodes',
		iconClassName: 'fa-heartbeat',
		breadcrumb: 'Diagnosis Codes',
	},
	collection: {
		id: 5,
		route: 'collection',
		iconClassName: 'fa-shopping-basket',
		breadcrumb: 'Sample Collection',
	},
	patient: {
		id: 6,
		route: 'patient',
		iconClassName: 'fa-user',
		breadcrumb: 'Patient',
	},
	insurance: {
		id: 7,
		route: 'insurance',
		iconClassName: 'fa-id-card-o',
		breadcrumb: 'Insurance',
	},
	practitioner_confirmation: {
		id: 8,
		route: 'confirm',
		iconClassName: 'fa-building',
		breadcrumb: 'Review',
	},
	patient_confirmation: {
		id: 9,
		route: 'confirm',
		iconClassName: 'fa-building',
		breadcrumb: 'Review',
	},
	payment: {
		id: 10,
		route: 'payment',
		iconClassName: 'fa-credit-card-alt',
		breadcrumb: 'Payment',
	},
	summary: {
		id: 11,
		route: 'summary',
		iconClassName: 'fa-send',
		breadcrumb: 'Summary',
	},
	handoff: {
		id: 12,
		route: 'handoff',
		iconClassName: 'fa-arrows-h',
		breadcrumb: 'Route',
	},
	customize: {
		id: 13,
		route: 'customize',
		iconClassName: 'fa-pencil-square-o',
		breadcrumb: 'Customize',
	},
	shipping: {
		id: 14,
		route: 'shipping',
		iconClassName: 'fa-truck',
		breadcrumb: 'Shipping',
	},
	practitioner_summary: {
		id: 15,
		route: 'registration',
		iconClassName: 'fa-user-md',
		breadcrumb: 'Practitioner Summary',
	},
};

/**
 * Gets the entry in this.workflowMetaData with the given 'id' value.
 *
 * @param {number} id The value of the 'id' property to match
 */
function getWorkflowMetaDataById(id) {
	return _getWorkflowMetaDataByProperty('id', id);
}

/**
 * Gets the entry in this.workflowMetaData with the given 'route' value.
 * @param {string} route The value of the 'route' property to match
 */
function getWorkflowMetaDataByRoute(route) {
	return _getWorkflowMetaDataByProperty('route', route);
}

/**
 * Gets the entry in this.workflowMetaData for the given key.
 * @param {string} key The key to match
 */
function getWorkflowMetaDataByKey(key) {
	return workflowMetaData[key];
}

/**
 * Gets the entry in this.workflowMetaData that matches the input property name and value.
 *
 * @param {string} name The property name to match
 * @param value The value to match
 * @return The matching workflow, or undefined if no match is found
 */
function _getWorkflowMetaDataByProperty(name, value) {
	return Object.values(workflowMetaData).find((step) => {
		return step[name] === value;
	});
}

/**
 * Returns whether the order has a completed Practitioner Confirmation step
 *
 * @param gearOrder the order to check
 */
function _isPractitionerConfirmed(gearOrder) {
	return gearOrder && gearOrder.workflow && gearOrder.workflow.practitionerConfirmed;
}

/**
 * Checks if the given step is valid for the current user type.
 * For example, a 'prc' user should never see the 'Practitioner Confirmation' step.
 */
function isStepValidForUser(workflowStep, userInfo, gearOrder) {
	if (!userInfo || !userInfo.type || !workflowStep) {
		return false;
	}

	// Make sure the step is one defined in workflowMetaData. This fixes problems when new steps are introduced between releases.
	if (!getWorkflowMetaDataById(workflowStep.id)) {
		return false;
	}

	// SCR-6204: Never show payment step. Payment was moved to confirmation page
	if (workflowStep.id === 10) {
		return false;
	}

	const accountType = userInfo.type;

	// Return false if the step is not applicable
	if (workflowStep.notApplicable) {
		return false;
	}

	// Route step should never show up
	if (workflowStep.id === 12) {
		return false;
	} else if (accountType === 'myGDX') {
		if (
			workflowStep.id === 9 || // Patient Confirmation
			workflowStep.id === 15 // Practitioner Confirmation (PRC)
		) {
			return false;
		}
		
		// Do not show Billing Options for myGDX accounts for Medicare orders
		if (workflowStep.id === 3 && isGovernmentPayorOrder(gearOrder)) {
			return false;
		}

		// Do not show Billing Options if 'Client' is selected and is the only option
		if (
			workflowStep.id === 3 && // Billing Options
			_canSkipClientBillingStep(gearOrder)
		) {
			return false;
		}

		return true;
	} else if (accountType === 'prc') {
		if (
			workflowStep.id === 8 || // Practitioner Confirmation
			workflowStep.id === 1 || // Practitioner
			workflowStep.id === 4 || // Diagnosis
			workflowStep.id === 13 || // Customize
			workflowStep.id === 14 // Shipping
		) {
			return false;
		}

		// Do not show Billing Options if 'Client' is selected and is the only option
		if (
			workflowStep.id === 3 && // Billing Options
			_canSkipClientBillingStep(gearOrder)
		) {
			return false;
		}

		const practitionerConfirmed = _isPractitionerConfirmed(gearOrder);
		if (
			workflowStep.id === 15 // Practitioner Confirmation (PRC)
		) {
			// Only show the Practitioner Overview step if the order has incomplete steps
			return practitionerConfirmed &&
			gearOrder?.workflow?.steps.some(step=>step.id !== 15 && step.id < 9 && !step.completed && isStepValidForUser(step, userInfo, gearOrder))
			;
		} else if (
			workflowStep.id === 2  // Panels
		) {
			return !practitionerConfirmed;
		}
	}
	return true;
}

/**
 * Returns whether all requisitions on the order have the 'Client' billing method selected and
 *  that is the only available option.
 *
 * @param gearOrder the order to check
 */
function _canSkipClientBillingStep(gearOrder) {
	return gearOrder.requisitions?.length &&
		gearOrder.requisitions.every(
			(req) => req.billingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE
		) &&
		gearOrder.practitioner?.billingMethods?.length === 1 &&
		gearOrder.practitioner.billingMethods[0] === 'Client';
}

/**
 * Returns true if the practitioner has saved any steps on the order
 */
 const isClientOrder = (gearOrder) => {
	return (
		gearOrder?.id &&
		(gearOrder?.workflow?.practitionerConfirmed ||
			gearOrder?.workflow?.steps?.some((step) => step.completed && step.userType === 'myGDX'))
	);
}

/**
 * Get a list of all steps that the user has access to that are not 'Completed' or 'N/A'.
 * This is different from workflow.complete because it accounts for the user type and only 
 * validates steps the user has access to.
 */
const getIncompleteUserSteps = (gearOrder, userInfo) => {
	if (!gearOrder?.workflow) {
		return [];
	}
	return gearOrder.workflow.steps.filter(
		(step) =>
			!(step.completed || step.notApplicable) &&
			step.id !== workflowMetaData.summary.id &&
			// Include the payment step even though it is not part of the navigation
			(isStepValidForUser(step, userInfo, gearOrder) || step.id === workflowMetaData.payment.id )
	);
} 

/* Exported object with public functions and objects */
const workflowUtil = {
	workflowMetaData: workflowMetaData,
	getIncompleteUserSteps: getIncompleteUserSteps,
	getWorkflowMetaDataById: getWorkflowMetaDataById,
	getWorkflowMetaDataByKey: getWorkflowMetaDataByKey,
	getWorkflowMetaDataByRoute: getWorkflowMetaDataByRoute,
	isStepValidForUser: isStepValidForUser,
	isClientOrder
};

export default workflowUtil;
